import { useCallback, useEffect, useMemo, useState } from "react";

import { usersService } from "@/modules/dashboard/users/services/users.service";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentRoutesService } from "../services/paymentRoutes.service";
import type { ProviderRailRoute } from "../types/paymentRoutes.types";

const AUTHORS_PAGE_SIZE = 200;
/** 5 × 200 — well above any real staff directory, so the map is complete in practice. */
const AUTHORS_MAX_PAGES = 5;

export interface UseRoutesCatalogResult {
  routes: ProviderRailRoute[];
  isLoading: boolean;
  hasError: boolean;
  accessDenied: boolean;
  truncated: boolean;
  refresh: () => void;
}

/**
 * Active provider routes — the authoritative corridor list.
 *
 * Coverage and margin are meaningless without it, so a failure is reported rather than papered over: the
 * Corridors tab shows an explicit error and the coverage KPI renders "—". The rule tabs stay usable, which is
 * why this is a separate hook from the fee list instead of one combined loading state.
 */
export function useRoutesCatalog(): UseRoutesCatalogResult {
  const [routes, setRoutes] = useState<ProviderRailRoute[]>([]);
  const [hasError, setHasError] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Loading is DERIVED (an attempt was requested but has not settled) — no synchronous setState in the effect.
  const [settledKey, setSettledKey] = useState<number | null>(null);
  const isLoading = settledKey !== reloadKey;

  useEffect(() => {
    let cancelled = false;
    PaymentRoutesService.listActive()
      .then((result) => {
        if (cancelled) return;
        setRoutes(result.routes);
        setTruncated(result.truncated);
        setHasError(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (HttpApiService.isAccessDeniedError(error)) setAccessDenied(true);
        else setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setSettledKey(reloadKey);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return {
    routes,
    isLoading,
    hasError,
    accessDenied,
    truncated,
    refresh: useCallback(() => setReloadKey((key) => key + 1), []),
  };
}

export interface UseFeeAuthorsResult {
  /** `created_by_user_id` → full name. Missing entries degrade to a shortened uuid at the call site. */
  nameById: Record<string, string>;
}

/**
 * Names for the `created_by_user_id` column. Requires `users:read`, which not every principal that can read
 * fees has — a denial is swallowed on purpose: the version stack still identifies the author by the uuid
 * tail, and an audit column must never block the price book from rendering.
 *
 * The directory is paged through in full rather than capped at one page: a partial map would silently render
 * some authors as uuids while others show a name, with nothing on screen explaining the difference. Should
 * the page budget ever be exhausted, the remaining rows still degrade visibly (uuid tail + full id in a
 * `title`), which is a disclosure in itself — no figure computed anywhere depends on this map.
 */
export function useFeeAuthors(): UseFeeAuthorsResult {
  const [nameById, setNameById] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;

    const loadAuthors = async () => {
      const map: Record<string, string> = {};
      for (let page = 0; page < AUTHORS_MAX_PAGES; page += 1) {
        const response = await usersService.fetchList({
          limit: AUTHORS_PAGE_SIZE,
          skip: page * AUTHORS_PAGE_SIZE,
        });
        if (cancelled) return;
        for (const user of response.data) map[user.id] = user.full_name;
        if (!response.hasMore || response.data.length === 0) break;
      }
      if (!cancelled) setNameById(map);
    };

    void loadAuthors().catch(() => {
      // Intentionally silent — see the doc comment.
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => ({ nameById }), [nameById]);
}
